/** `world.heightfield` — a seeded jagged rock profile spanning the world's
 * width, broken by flat landing pads: one wide, low-multiplier SAFETY pad in
 * the slot over a declared column (where the player appears), the other
 * slots filled from a pool of narrower, richer pads in seeded order, laid
 * left to right with raw rock between them. Each pad is flattened onto the
 * rock at its own height, the rock beside it is capped into a ramp (an
 * approach is never a wall), and its landable deck is shrunk around its
 * centre by an optional width scalar (later fields demand more precision),
 * never below a minimum width. Pads are placed as entities of a declared
 * kind (sized to the deck, captioned with their multiplier); the profile is
 * published as ground the platform draws. A named signal (a phase start)
 * regenerates the field. Deterministic: every draw comes from the world RNG. */
import type { JsonObject } from '../../json.js';
import { id, int, list, num, obj, readsSignal } from '../schemaBuilders.js';
import { defineModule, num as read, str, type ModuleCtx } from '../module.js';
import { clamp } from '../physics.js';
import type { WorldV1 } from '../world.js';

export type PadSpecV1 = { cells: number; mult: number };

export type HeightfieldConfig = {
  /** Profile cells across the world width (cells + 1 points). */
  cells: number;
  /** Mean rock level, the highest peak and the deepest floor (y grows down). */
  base: number; peak: number; floor: number;
  /** Largest random step between neighbouring points, and the pull back toward `base` (0..1). */
  jitter: number; pull: number;
  /** The always-present wide pad, in the slot over column `safeX`. */
  safetyPad: PadSpecV1;
  safeX: number;
  /** Pads per field (the safety pad included); the rest come from `pool`. */
  count: number;
  pool: PadSpecV1[];
  /** Raw-rock cells between neighbouring pads. */
  gapCells: number;
  /** How much lower (y) each of the two rock points beside a pad must sit. */
  rampDrop: number;
  pad: string;
  padHeight: number;
  minPadWidth: number;
  /** Deck width multiplier read when a field is generated (absent: 1). */
  widthScalar?: string;
  /** Signal kind that generates the next field. */
  regenerateOn: string;
  /** Ground palette indices: rock fill and its top edge. */
  fill: number; edge: number;
};

/** One generated pad: its landable deck [x0, x1] at height `y`. */
export interface TerrainPadV1 { entity: string; x0: number; x1: number; y: number; mult: number }
/** A field as stored in the module's global state. */
export interface TerrainV1 { field: number; step: number; heights: number[]; pads: TerrainPadV1[] }

const padSchema = obj({ cells: int(1, 32), mult: int(1, 99) });

/** The field a heightfield instance (by key) currently holds. */
export function terrainOf(world: Readonly<WorldV1>, key: string): TerrainV1 | null {
  const s = world.globals[key];
  if (!s || !Array.isArray(s.heights)) return null;
  const pads = (s.pads as JsonObject[]).map(p => ({ entity: str(p, 'entity')!, x0: read(p, 'x0'), x1: read(p, 'x1'), y: read(p, 'y'), mult: read(p, 'mult') }));
  return { field: read(s, 'field'), step: read(s, 'step'), heights: s.heights as number[], pads };
}

/** Rock height (y) under world x — linear between profile points. */
export function heightAt(terrain: TerrainV1, x: number): number {
  const last = terrain.heights.length - 1;
  const t = clamp(x / terrain.step, 0, last);
  const i = Math.min(Math.floor(t), last - 1), f = t - i;
  return terrain.heights[i]! * (1 - f) + terrain.heights[i + 1]! * f;
}

type Ctx = ModuleCtx<HeightfieldConfig>;

function generate(ctx: Ctx, field: number): void {
  const c = ctx.config, s = ctx.state;
  for (const pad of (s.pads ?? []) as JsonObject[]) {
    const old = ctx.entity(str(pad, 'entity'));
    if (old) ctx.despawn(old, 'field-end');
  }
  const step = ctx.world.bounds.w / c.cells;
  const span = (lo: number, hi: number) => lo + (hi - lo) * ctx.random();
  const heights: number[] = [];
  let h = c.base + span(-c.jitter / 2, c.jitter / 2);
  for (let i = 0; i <= c.cells; i++) {
    const y = clamp(h, c.peak, c.floor);
    heights.push(y);
    h = y + span(-c.jitter, c.jitter) + (c.base - y) * c.pull;
  }
  const deck = heights.map(() => false);
  // The safety pad takes the slot over the safe column; the others draw
  // from the pool without replacement.
  const safeSlot = clamp(Math.floor((c.safeX / step) / (c.cells / c.count)), 0, c.count - 1);
  const pool = [...c.pool];
  const specs: PadSpecV1[] = [];
  for (let slot = 0; slot < c.count; slot++) {
    if (slot === safeSlot) { specs.push(c.safetyPad); continue; }
    specs.push(pool.splice(ctx.randomInt(pool.length), 1)[0]!);
  }
  let slack = Math.max(0, c.cells - specs.reduce((n, p) => n + p.cells, 0) - c.gapCells * (specs.length - 1));
  let cursor = 0;
  const shrink = clamp(c.widthScalar === undefined ? 1 : ctx.scalar(c.widthScalar, 1), 0, 1);
  const pads: JsonObject[] = [];
  for (const spec of specs) {
    const jitter = ctx.randomInt(slack + 1);
    slack -= jitter; cursor += jitter;
    const start = cursor;
    cursor += spec.cells + c.gapCells;
    let sum = 0;
    for (let j = start; j <= start + spec.cells; j++) sum += heights[j]!;
    const py = clamp(sum / (spec.cells + 1), c.peak, c.floor);
    for (let j = start; j <= start + spec.cells; j++) { heights[j] = py; deck[j] = true; }
    for (let d = 1; d <= 2; d++) {
      const limit = py - c.rampDrop * d;
      for (const j of [start - d, start + spec.cells + d]) if (j >= 0 && j <= c.cells && !deck[j]) heights[j] = Math.max(heights[j]!, limit);
    }
    const width = Math.max(c.minPadWidth, spec.cells * step * shrink);
    const cx = (start + spec.cells / 2) * step;
    const entity = ctx.spawn(c.pad, { x: cx, y: py + c.padHeight, params: { w: width, h: c.padHeight }, label: `x${spec.mult}` });
    pads.push({ entity: entity.id, x0: cx - width / 2, x1: cx + width / 2, y: py, mult: spec.mult });
  }
  Object.assign(s, { field, step, heights, pads });
}

export const heightfield = defineModule<HeightfieldConfig>({
  id: 'world.heightfield', version: 1, attach: 'global', role: 'world', surfaceContact: true,
  summary: 'Seeded jagged terrain across the world width with flat landing pads (a wide safety pad over a declared column, richer narrower pads from a pool), shrinking decks by a scalar, regenerated on a signal; published as drawn ground.',
  config: obj({
    cells: int(4, 64), base: num(0, 64), peak: num(0, 64), floor: num(0, 64), jitter: num(0, 4), pull: num(0, 1),
    safetyPad: padSchema, safeX: num(0, 64), count: int(1, 6), pool: list(padSchema, 8, 1), gapCells: int(0, 16), rampDrop: num(0, 4),
    pad: id(), padHeight: num(0.05, 2), minPadWidth: num(0.1, 16), widthScalar: id(), regenerateOn: readsSignal(),
    fill: int(0, 15), edge: int(0, 15),
  }, ['widthScalar']),
  requires: ['body.static'], events: [], presentation: {},
  proofs: ['deterministic-per-seed', 'safety-pad-over-safe-column', 'pads-flat-and-separated', 'ramps-beside-pads', 'decks-shrink-by-scalar',
    'regenerates-on-signal', 'publishes-ground'],
  animStates: () => [],
  mapArchetypeRefs: (c, rename) => ({ ...c, pad: rename(c.pad) }),
  check(c, context) {
    const errors: string[] = [];
    if (!(c.peak <= c.base && c.base <= c.floor)) errors.push('expected peak ≤ base ≤ floor (y grows down)');
    if (c.floor > context.world.h) errors.push('the rock floor must lie inside the world');
    if (c.safeX > context.world.w) errors.push('safeX must lie inside the world');
    if (c.count - 1 > c.pool.length) errors.push('the pool must hold a pad for every non-safety slot');
    const widest = [...c.pool].map(p => p.cells).sort((a, b) => b - a).slice(0, c.count - 1).reduce((n, k) => n + k, 0);
    if (c.safetyPad.cells + widest + c.gapCells * (c.count - 1) > c.cells) errors.push('the pads and gaps must fit the field');
    if (!context.archetypes[c.pad]?.modules.includes('body.static')) errors.push(`${c.pad} must use body.static`);
    if (c.fill >= context.paletteSize || c.edge >= context.paletteSize) errors.push('ground colours must index the palette');
    return errors;
  },
  initGlobal(ctx) { generate(ctx, 1); },
  systems: {
    // Flow: after the director that starts the phase this field belongs to.
    flow(ctx) {
      const started = ctx.signals(ctx.config.regenerateOn);
      if (!started.length) return;
      const phase = started[0]!.data.phase;
      const field = typeof phase === 'number' ? phase : read(ctx.state, 'field') + 1;
      if (field !== read(ctx.state, 'field')) generate(ctx, field);
    },
  },
  ground(ctx) {
    const s = ctx.state, step = read(s, 'step');
    return [{ points: (s.heights as number[]).flatMap((y, i) => [i * step, y]), fill: ctx.config.fill, edge: ctx.config.edge }];
  },
});
